import { Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import tw from "twin.macro";

import { useAreas } from "@/api/areas/queries";
import SearchInput from "@/components/SearchInput";

const LAZY_PAGE_SIZE = 5;

type ManualModuleSearchProps = {
  onSelected: (areaId: string, moduleAddress: string) => void;
};

type FlatModule = {
  address: number;
  areaId: string;
  areaName: string;
};

const ManualModuleSearch = ({ onSelected }: ManualModuleSearchProps) => {
  const { t } = useTranslation();
  const { data: areas } = useAreas();
  const [query, setQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(LAZY_PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const allModules = useMemo<FlatModule[]>(() => {
    if (!areas) return [];
    return areas.flatMap((area) =>
      (area.posts ?? []).map((post) => ({
        address: post.address,
        areaId: area._id,
        areaName: area.name,
      }))
    );
  }, [areas]);

  const filtered = useMemo(() => {
    const q = query.trim();
    if (!q) return allModules;
    return allModules.filter((m) => String(m.address).includes(q));
  }, [allModules, query]);

  // Reset visible count whenever the query changes
  useEffect(() => {
    setVisibleCount(LAZY_PAGE_SIZE);
  }, [query]);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  // IntersectionObserver-based lazy loading
  useEffect(() => {
    if (!hasMore || !sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((n) => n + LAZY_PAGE_SIZE);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, visible.length]);

  // Group visible modules by area
  const groups = useMemo(() => {
    const map = new Map<string, { areaId: string; areaName: string; modules: FlatModule[] }>();
    for (const m of visible) {
      const existing = map.get(m.areaId);
      if (existing) {
        existing.modules.push(m);
      } else {
        map.set(m.areaId, { areaId: m.areaId, areaName: m.areaName, modules: [m] });
      }
    }
    return [...map.values()];
  }, [visible]);

  return (
    <div tw="flex flex-col gap-3 px-4 pb-6">
      <div tw="flex flex-col gap-1.5">
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder={t("quickModuleAccess.searchPlaceholder")}
        />
        {query.trim().length > 0 && (
          <p tw="text-xs text-gray-400 px-1">
            {filtered.length} / {allModules.length}
          </p>
        )}
      </div>

      <div tw="overflow-y-auto max-h-[55vh] flex flex-col rounded-xl border border-gray-100">
        {groups.length === 0 ? (
          <div tw="flex flex-col items-center justify-center py-12 gap-2 text-gray-400">
            <Search tw="w-8 h-8 opacity-40" />
            <p tw="text-sm">{t("quickModuleAccess.noModuleFound")}</p>
          </div>
        ) : (
          groups.map(({ areaId, areaName, modules }) => (
            <div key={areaId}>
              <div tw="px-4 py-2 bg-primary-50 text-xs font-semibold text-primary-800 sticky top-0">
                {areaName} ({modules.length} / {(areas?.find((a) => a._id === areaId)?.posts ?? []).length})
              </div>
              <div tw="divide-y divide-gray-50">
                {modules.map((m) => (
                  <button
                    key={m.address}
                    type="button"
                    tw="w-full flex items-center justify-between px-4 py-3 active:bg-gray-50 transition text-left"
                    onClick={() => onSelected(m.areaId, String(m.address))}
                  >
                    <span tw="text-sm text-gray-800 font-medium">
                      {m.address}
                    </span>
                    <div
                      css={[
                        tw`w-8 h-8 rounded-full flex items-center justify-center bg-primary-700`,
                      ]}
                    >
                      <Search tw="w-4 h-4 text-white" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))
        )}

        {hasMore && <div ref={sentinelRef} tw="h-4 shrink-0" />}
      </div>
    </div>
  );
};

export default ManualModuleSearch;
